import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate, Link} from 'react-router-dom';
import {PASSWORD_REQUIREMENTS, validateStrongPassword} from '@common/validation/strongPassword';
import {useAuthStore} from '@common-web/stores/authStore';
import {toast} from '@common-web/hooks/use-toast';
import {Button} from '@common-web/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@common-web/components/ui/card';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {trpc} from '@common-web/lib/trpc';
import {UserPlus, Mail, Lock, Key, ArrowRight} from 'lucide-react';
import {AuthPageLayout} from '@common-web/components/auth-page-layout';

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  code: string;
}

export function SignupPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isLoading, setIsLoading] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);

  const {
    register,
    handleSubmit,
    formState: {errors},
    watch,
  } = useForm<SignupFormData>({
    defaultValues: {
      code: searchParams.get('accessCode') || '',
      email: decodeURIComponent(searchParams.get('email') || ''),
    },
  });

  const password = watch('password');

  const signupMutation = trpc.auth.signupWithCode.useMutation({
    onSuccess: async (data) => {
      if (data.success && data.user) {
        toast({
          title: '✅ Registration Successful',
          description:
            ' Your portal account has been created.\n' +
            ' Please log in now with the credentials you just set up.',
        });
        navigate('/login');
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Signup failed',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await signupMutation.mutateAsync({
        email: data.email,
        password: data.password,
        code: data.code,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout heroPosition='right'>
      <div className='w-full max-w-md'>
        <Card className='border-gray-200 shadow-xl'>
          <CardHeader className='space-y-1 pb-6'>
            <div className='flex items-center justify-center mb-4'>
              <div className='p-3 bg-brand-navy/10 rounded-full'>
                <UserPlus className='h-6 w-6 text-brand-navy' />
              </div>
            </div>
            <CardTitle className='text-2xl text-center font-bold text-brand-navy'>
              Create Your Account
            </CardTitle>
            <CardDescription className='text-center text-brand-gray'>
              Enter your registration code to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='flex flex-col gap-5'>
                <div className='space-y-2'>
                  <Label htmlFor='code' className='text-brand-navy font-medium'>
                    Registration Code
                  </Label>
                  <div className='relative'>
                    <Key className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='code'
                      type='text'
                      placeholder='Enter your unique code'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue font-mono uppercase'
                      disabled={isLoading}
                      {...register('code', {
                        required: 'Registration code is required',
                        minLength: {
                          value: 6,
                          message: 'Code must be at least 6 characters',
                        },
                      })}
                    />
                  </div>
                  {errors.code && (
                    <p className='text-sm text-red-600 mt-1'>{errors.code.message}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='email' className='text-brand-navy font-medium'>
                    Email Address
                  </Label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='email'
                      type='email'
                      placeholder='you@example.com'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className='text-sm text-red-600 mt-1'>{errors.email.message}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='password' className='text-brand-navy font-medium'>
                    Password
                  </Label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='password'
                      type='password'
                      placeholder='••••••••'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('password', {
                        required: 'Password is required',
                        validate: validateStrongPassword,
                      })}
                    />
                  </div>
                  {errors.password && (
                    <p className='text-sm text-red-600 mt-1'>{errors.password.message}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='confirmPassword' className='text-brand-navy font-medium'>
                    Confirm Password
                  </Label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='confirmPassword'
                      type='password'
                      placeholder='••••••••'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => value === password || 'Passwords do not match',
                      })}
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className='text-sm text-red-600 mt-1'>{errors.confirmPassword.message}</p>
                  )}
                </div>

                <div className='p-3 bg-blue-50 rounded-lg border border-blue-200'>
                  <p className='text-xs font-bold text-blue-800'>Password requirements:</p>
                  <ul className='list-disc pl-4 text-xs text-blue-800'>
                    {PASSWORD_REQUIREMENTS.map((requirement) => (
                      <li key={requirement}>{requirement}</li>
                    ))}
                  </ul>
                </div>

                <Button
                  type='submit'
                  className='w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold transition-colors shadow-sm'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className='flex items-center gap-2'>
                      <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                      Creating account...
                    </span>
                  ) : (
                    <span className='flex items-center justify-center gap-2'>
                      Create Account
                      <ArrowRight className='h-4 w-4' />
                    </span>
                  )}
                </Button>
              </div>

              <div className='mt-6 text-center'>
                <p className='text-sm text-brand-gray'>
                  Already have an account?{' '}
                  <Link
                    to='/login'
                    className='text-brand-blue hover:text-brand-blue-dark font-semibold transition-colors'
                  >
                    Sign in instead
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className='text-center text-xs text-brand-gray mt-6'>
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </AuthPageLayout>
  );
}
